import {
  GroupEntity,
  UserEntity,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  Box,
  createStyles,
  Grid,
  IconButton,
  makeStyles,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core';
import useAsync from 'react-use/lib/useAsync';

import {
  Avatar,
  InfoCard,
  Progress,
  ResponseErrorPanel,
  Link,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { EntityRefLink } from '@backstage/plugin-catalog-react';

import EditIcon from '@material-ui/icons/Edit';
import HelpButton from '@material-ui/icons/Help';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardOutline: {
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[2],
      borderRadius: '4px',
      overflow: 'visible',
      position: 'relative',
      margin: theme.spacing(4, 1, 1),
      flex: '1',
      minWidth: '0px',
    },
    email: {
      display: 'inline-block',
      maxWidth: '100%',
      fontSize: 14,
    },
    card: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '15px',
    },
  }),
);

const MemberComponent = (props: { member: UserEntity }) => {
  const classes = useStyles();
  const {
    metadata: { name: metaName, description },
    spec: { profile },
  } = props.member;
  const displayName = profile?.displayName ?? metaName;

  return (
    <Grid item container md={12} lg={6}>
      <Box className={classes.cardOutline}>
        <Box className={classes.card}>
          <Avatar
            displayName={displayName}
            picture={profile?.picture}
            customStyles={{
              position: 'absolute',
              top: '-2rem',
            }}
          />
          <Box
            pt={2}
            sx={{
              maxWidth: '100%',
            }}
            textAlign="center"
          >
            <Typography>
              <EntityRefLink
                entityRef={props.member}
                defaultKind="user"
                title={displayName}
              />
            </Typography>
            {profile?.email && (
              <Link className={classes.email} to={`mailto:${profile.email}`}>
                {profile.email}
              </Link>
            )}
            {description && (
              <Typography variant="subtitle2">{description}</Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Grid>
  );
};

/** @public */
export const MembersListCard = () => {
  const { entity: groupEntity } = useEntity<GroupEntity>();
  const {
    metadata: { name: groupName },
  } = groupEntity;
  const catalogApi = useApi(catalogApiRef);

  const {
    loading,
    error,
    value: members,
  } = useAsync(async () => {
    const membersList = await catalogApi.getEntities({
      filter: {
        kind: 'User',
        'relations.memberof': [
          stringifyEntityRef({
            kind: 'group',
            name: groupName.toLocaleLowerCase('en-US'),
          }),
        ],
      },
    });

    return membersList.items as UserEntity[];
  }, [catalogApi, groupEntity]);

  if (loading) {
    return <Progress />;
  } else if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return (
    <Grid item>
      <InfoCard
        title={
          <>
            Members
            <Tooltip title="Team Members can be updated by editing the Team Roster in Jira. Changes may take up to 48 hours to reflect in DevOps Central.">
              <HelpButton color="primary" style={{ paddingLeft: '0.2rem' }} />
            </Tooltip>
          </>
        }
        action={
          <IconButton
            component={Link}
            aria-label="Edit"
            title="Edit Jira Team Roster"
            to={groupEntity.metadata.annotations?.['backstage.io/view-url']!}
          >
            <EditIcon />
          </IconButton>
        }
      >
        <Grid container spacing={3}>
          {members && members.length > 0 ? (
            members.map(member => (
              <MemberComponent member={member} key={member.metadata.uid} />
            ))
          ) : (
            <Box p={2}>
              <Typography>This group has no members</Typography>
            </Box>
          )}
        </Grid>
      </InfoCard>
    </Grid>
  );
};
